import { useCallback, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { terraProxyApiClient } from '../clients/testerClient';
import { useSelectedTarget } from '../store/projectStore';
import type { z } from 'zod';
import type {
  NetworkRequestBatchSchema,
  NetworkRequestSchema,
  TrackerSkipReason,
} from '@terra/shared-contracts-tracker';
import { TRACKER_SKIP_REASON_OUT_OF_SCOPE } from '@terra/shared-contracts-tracker';
import { toasterApi } from '@terra/ui-design-system';
import {
  buildHarPostData,
  buildHarResponseContent,
  computePortalBodyByteSize,
  normalizeHarHttpVersion,
} from '@terra/shared-utils';
import { estimateBase64ByteSize } from '../electron-shared/utils/binary-body';

type NetworkTrackingPayload = z.infer<typeof NetworkRequestSchema>;
type NetworkTrackingBatchPayload = z.infer<typeof NetworkRequestBatchSchema>;

type TrackRequestInput = {
  request: TrackableRequest;
  response?: TrackableResponse;
};

export type TrackableRequest = {
  method: string;
  url: string;
  headers: Record<string, string | string[]>;
  body?: string;
  bodyEncoding?: 'utf8' | 'base64';
  httpVersion?: string;
  timestamp?: number;
};

export type TrackableResponse = {
  statusCode: number;
  statusMessage: string;
  headers: Record<string, string | string[]>;
  body: string;
  duration?: number;
  httpVersion?: string;
  timestamp?: number;
};

/**
 * Helper to extract status code from various request object formats
 * Handles: ProxyRequest.status, ProxyRequest.statusCode, HARRequest.status
 */
export function extractStatusCode(requestOrResponse: {
  statusCode?: number;
  status?: number;
  responseStatusCode?: number;
}): number {
  return (
    requestOrResponse.statusCode ??
    requestOrResponse.status ??
    requestOrResponse.responseStatusCode ??
    0
  );
}

function showTrackRequestSkipToast({
  skipReason,
  message,
  t,
}: {
  skipReason?: TrackerSkipReason;
  message?: string;
  t: ReturnType<typeof useTranslations>;
}) {
  if (skipReason === TRACKER_SKIP_REASON_OUT_OF_SCOPE) {
    toasterApi.warning({
      title: t('trackRequestOutOfScopeTitle'),
      description: t('trackRequestOutOfScopeDescription'),
    });

    return;
  }

  toasterApi.warning({
    title: t('trackRequestNotTrackedTitle'),
    description: message ?? t('trackRequestNotTrackedDescription'),
  });
}

export function useTrackRequest() {
  const t = useTranslations('httpHistory.actions');
  const tBulk = useTranslations('httpHistory.bulkActions');
  const selectedTarget = useSelectedTarget();
  const [isTracking, setIsTracking] = useState(false);
  const inFlightTrackCountRef = useRef(0);

  const beginTracking = useCallback(() => {
    inFlightTrackCountRef.current += 1;
    setIsTracking(true);
  }, []);

  const endTracking = useCallback(() => {
    inFlightTrackCountRef.current = Math.max(
      0,
      inFlightTrackCountRef.current - 1,
    );
    setIsTracking(inFlightTrackCountRef.current > 0);
  }, []);

  const trackRequest = useCallback(
    async ({
      request,
      response,
      credentialId,
      hideToast,
    }: {
      request: TrackableRequest;
      response?: TrackableResponse;
      credentialId?: string;
      hideToast?: boolean;
    }) => {
      if (!selectedTarget) {
        if (!hideToast) {
          toasterApi.error({
            title: t('noTargetSelectedTitle'),
            description: t('noTargetSelectedDescription'),
          });
        }

        return { success: false, error: 'No target selected' };
      }

      beginTracking();

      try {
        const payload = convertToHARFormat({
          request,
          response,
          applicationId: selectedTarget.id,
          credentialId,
        });

        const result = await terraProxyApiClient.track(payload);

        const skipReason = result.skipReason;

        if (!hideToast) {
          if (result.success) {
            toasterApi.success({
              title: t('trackRequestSuccessTitle'),
              description: t('trackRequestSuccessDescription'),
            });
          } else {
            showTrackRequestSkipToast({
              skipReason,
              message: result.message,
              t,
            });
          }
        }

        return { success: result.success, skipReason };
      } catch (error) {
        console.error('Failed to track request:', error);
        if (!hideToast) {
          toasterApi.error({
            title: 'Failed to track request',
            description:
              error instanceof Error ? error.message : 'Unknown error occurred',
          });
        }

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      } finally {
        endTracking();
      }
    },
    [beginTracking, endTracking, selectedTarget, t],
  );

  const trackRequests = useCallback(
    async ({
      entries,
      credentialId,
      hideToast,
    }: {
      entries: TrackRequestInput[];
      credentialId?: string;
      hideToast?: boolean;
    }) => {
      if (!selectedTarget) {
        if (!hideToast) {
          toasterApi.error({
            title: t('noTargetSelectedTitle'),
            description: t('noTargetSelectedDescription'),
          });
        }

        return {
          success: false,
          error: 'No target selected',
          trackedCount: 0,
          failedCount: entries.length,
          skippedCount: 0,
        };
      }

      if (entries.length === 0) {
        return {
          success: false,
          error: 'No requests selected',
          trackedCount: 0,
          failedCount: 0,
          skippedCount: 0,
        };
      }

      const payload: NetworkTrackingBatchPayload = {
        applicationId: selectedTarget.id,
        requests: entries.map((entry) =>
          convertToHARFormat({
            request: entry.request,
            response: entry.response,
            applicationId: selectedTarget.id,
            credentialId,
          }),
        ),
      };

      beginTracking();

      try {
        const result = await terraProxyApiClient.trackBulk(payload);
        const trackedCount = result.trackedCount ?? 0;
        const skippedCount = result.skippedCount ?? 0;
        const failedCount = result.failedCount ?? 0;

        const bulkSkipReason = result.skipReason;

        if (!hideToast) {
          if (failedCount > 0) {
            toasterApi.error({
              title: tBulk('trackingErrorTitle'),
              description: result.message ?? tBulk('trackingErrorDescription'),
            });
          } else if (
            trackedCount === 0 &&
            failedCount === 0 &&
            skippedCount > 0 &&
            bulkSkipReason === TRACKER_SKIP_REASON_OUT_OF_SCOPE
          ) {
            toasterApi.warning({
              title: tBulk('trackingOutOfScopeTitle'),
              description: tBulk('trackingOutOfScopeDescription'),
            });
          } else if (trackedCount > 0 && skippedCount === 0) {
            toasterApi.success({
              title: tBulk('trackingSuccessTitle'),
              description: tBulk('trackingSuccessDescription', {
                count: trackedCount,
              }),
            });
          } else if (trackedCount > 0 || skippedCount > 0) {
            toasterApi.warning({
              title: tBulk('trackingPartialTitle'),
              description: tBulk('trackingPartialDescription', {
                succeeded: trackedCount,
                failed: failedCount,
                skipped: skippedCount,
              }),
            });
          } else {
            showTrackRequestSkipToast({
              skipReason: bulkSkipReason,
              message: result.message,
              t,
            });
          }
        }

        return result;
      } catch (error) {
        console.error('Failed to track requests:', error);
        if (!hideToast) {
          toasterApi.error({
            title: 'Failed to track requests',
            description:
              error instanceof Error ? error.message : 'Unknown error occurred',
          });
        }

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          trackedCount: 0,
          failedCount: entries.length,
          skippedCount: 0,
        };
      } finally {
        endTracking();
      }
    },
    [beginTracking, endTracking, selectedTarget, t, tBulk],
  );

  return { trackRequest, trackRequests, isTracking };
}

/**
 * Convert request/response to HAR format
 * Based on BackendTrackingService.convertToHARFormat
 */
function convertToHARFormat({
  request,
  response,
  applicationId,
  credentialId,
}: {
  request: TrackableRequest;
  response?: TrackableResponse;
  applicationId: string;
  credentialId?: string;
}): NetworkTrackingPayload {
  // Convert headers from object to HAR format
  const requestHeaders = Object.entries(request.headers).flatMap(
    ([name, value]) => {
      if (Array.isArray(value)) {
        return value.map((v) => ({ name, value: v }));
      }

      return [{ name, value }];
    },
  );

  const responseHeaders = response
    ? Object.entries(response.headers).flatMap(([name, value]) => {
        if (Array.isArray(value)) {
          return value.map((v) => ({ name, value: v }));
        }

        return [{ name, value }];
      })
    : [];

  // Parse query string from URL
  let queryString: Array<{ name: string; value: string }> = [];

  try {
    const url = new URL(request.url);

    queryString = Array.from(url.searchParams.entries()).map(
      ([name, value]) => ({
        name,
        value,
      }),
    );
  } catch {
    // Invalid URL, skip query string parsing
  }

  // Calculate timings
  const startTime = request.timestamp || Date.now();
  const endTime = response?.timestamp || startTime;
  const duration = response?.duration || endTime - startTime;
  const requestHttpVersion = normalizeHarHttpVersion(request.httpVersion);
  const responseHttpVersion = normalizeHarHttpVersion(
    response?.httpVersion ?? request.httpVersion,
  );

  const harPayload: NetworkTrackingPayload = {
    applicationId,
    credentialId,
    pageUrl: undefined,
    endpointSource: 'TRACKING',
    request: {
      method: request.method,
      url: request.url,
      httpVersion: requestHttpVersion,
      cookies: parseRequestCookies(request.headers),
      headers: requestHeaders,
      queryString,
      postData: buildHarPostData({
        body: request.body,
        bodyEncoding: request.bodyEncoding,
        headers: request.headers,
      }),
      headersSize: calculateHeadersSize(requestHeaders),
      bodySize: request.body
        ? request.bodyEncoding === 'base64'
          ? estimateBase64ByteSize(request.body)
          : computePortalBodyByteSize(request.body)
        : 0,
    },
    response: response
      ? {
          status: response.statusCode ?? 0,
          statusText: response.statusMessage || '',
          httpVersion: responseHttpVersion,
          cookies: parseResponseCookies(response.headers),
          headers: responseHeaders,
          content: (() => {
            const builtContent = buildHarResponseContent({
              body: response.body,
              headers: response.headers,
            });

            if (!builtContent) {
              return {
                size: 0,
                mimeType: (() => {
                  const ct = response.headers['content-type'];
                  const contentType = Array.isArray(ct) ? ct[0] : ct;

                  return contentType || '';
                })(),
                text: null,
              };
            }

            return {
              size: builtContent.size,
              mimeType: builtContent.mimeType,
              text: builtContent.text,
              ...(builtContent.encoding
                ? { encoding: builtContent.encoding }
                : {}),
            };
          })(),
          redirectURL: (() => {
            const loc = response.headers.location;

            return (Array.isArray(loc) ? loc[0] : loc) || '';
          })(),
          headersSize: calculateHeadersSize(responseHeaders),
          bodySize: response.body
            ? computePortalBodyByteSize(response.body)
            : 0,
        }
      : {
          status: 0,
          statusText: '',
          httpVersion: requestHttpVersion,
          cookies: [],
          headers: [],
          content: {
            size: 0,
            mimeType: '',
            text: null,
          },
          redirectURL: '',
          headersSize: 0,
          bodySize: 0,
        },
    timings: {
      blocked: -1,
      dns: -1,
      connect: -1,
      send: 0,
      wait: duration,
      receive: 0,
    },
  };

  return harPayload;
}

/**
 * Calculate headers size (approximation)
 */
function calculateHeadersSize(
  headers: Array<{ name: string; value: string }>,
): number {
  return headers.reduce((size, header) => {
    return size + header.name.length + header.value.length + 4; // +4 for ": " and "\r\n"
  }, 0);
}

/**
 * Parse cookies from Cookie header (request)
 */
function parseRequestCookies(
  headers: Record<string, string | string[]>,
): Array<{ name: string; value: string }> {
  const cookieHeader = headers.cookie || headers.Cookie;

  if (!cookieHeader) return [];

  const cookieString = Array.isArray(cookieHeader)
    ? cookieHeader[0]
    : cookieHeader;

  if (!cookieString) return [];

  return cookieString
    .split(';')
    .map((pair) => pair.trim())
    .filter((pair) => pair.length > 0)
    .map((pair) => {
      const [name, ...valueParts] = pair.split('=');

      return {
        name: name.trim(),
        value: valueParts.join('=').trim(),
      };
    })
    .filter((cookie) => cookie.name && cookie.value);
}

/**
 * Parse cookies from Set-Cookie headers (response)
 */
function parseResponseCookies(
  headers: Record<string, string | string[]>,
): Array<{
  name: string;
  value: string;
  path?: string;
  domain?: string;
  expires?: string;
  httpOnly?: boolean;
  secure?: boolean;
}> {
  const setCookieHeaders = headers['set-cookie'] || headers['Set-Cookie'];

  if (!setCookieHeaders) return [];

  const cookieStrings = Array.isArray(setCookieHeaders)
    ? setCookieHeaders
    : [setCookieHeaders];

  return cookieStrings
    .map((cookieString) => {
      const parts = cookieString.split(';').map((part) => part.trim());

      if (parts.length === 0) return null;

      const [name, ...valueParts] = parts[0].split('=');
      const cookie: {
        name: string;
        value: string;
        path?: string;
        domain?: string;
        expires?: string;
        httpOnly?: boolean;
        secure?: boolean;
      } = {
        name: name.trim(),
        value: valueParts.join('=').trim(),
      };

      // Parse additional attributes
      for (let i = 1; i < parts.length; i++) {
        const part = parts[i];
        const [key, value] = part.split('=').map((s) => s.trim());
        const lowerKey = key.toLowerCase();

        if (lowerKey === 'path') {
          cookie.path = value;
        } else if (lowerKey === 'domain') {
          cookie.domain = value;
        } else if (lowerKey === 'expires') {
          cookie.expires = value;
        } else if (lowerKey === 'httponly') {
          cookie.httpOnly = true;
        } else if (lowerKey === 'secure') {
          cookie.secure = true;
        }
      }

      return cookie;
    })
    .filter(
      (
        cookie,
      ): cookie is {
        name: string;
        value: string;
        path?: string;
        domain?: string;
        expires?: string;
        httpOnly?: boolean;
        secure?: boolean;
      } => cookie !== null && !!cookie.name && !!cookie.value,
    );
}
